import type { CarrierGroup } from '@commissionsight/sdk';
import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';

export interface CarrierOption {
  id: string;
  name: string;
  slug: string;
  /** Brand this carrier belongs to (null/undefined = standalone). */
  groupId?: string | null;
}

/**
 * The single source for every carrier picker in the app. Returns the account's
 * carriers restricted to those with an active, ingestable config
 * (`withConfig: true`), so the same carrier set — and the same names — appear on
 * every screen (Dashboard, Audit, Compare, Chargebacks, Files, Rates, SFTP).
 *
 * Restricting to configured carriers is deliberate: a carrier with no active
 * config can never have ingested statement data, so it has no place in a filter
 * or upload picker. Using one hook everywhere keeps the carrier list "mapped to
 * ingest" and prevents the per-page drift that let some screens show every
 * carrier (e.g. "UHC") while others showed only the configured ones.
 */
export function useCarriers(opts: { withConfig?: boolean } = {}): {
  carriers: CarrierOption[];
  loading: boolean;
} {
  // Default to configured-only (every filter/upload picker). The carrier-setup
  // flow passes `withConfig: false` to list carriers that DON'T have a mapping yet.
  const withConfig = opts.withConfig ?? true;
  const { client } = useAuth();
  const [carriers, setCarriers] = useState<CarrierOption[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    client
      .listCarriers({ withConfig })
      .then((r) => {
        if (live) setCarriers(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [client, withConfig]);
  return { carriers, loading };
}

/**
 * Carrier brands (groups) that have at least one configured member — the
 * selectable brands for content-routed upload ("select UHC → detect the
 * product"). Empty for accounts with no groups, so the upload picker degrades to
 * a plain carrier list.
 */
export function useCarrierGroups(): { groups: CarrierGroup[]; loading: boolean } {
  const { client } = useAuth();
  const [groups, setGroups] = useState<CarrierGroup[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    client
      .listCarrierGroups()
      .then((r) => {
        if (live) setGroups(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [client]);
  return { groups, loading };
}
